import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Box,
    Alert,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    CircularProgress,
    Snackbar,
    Autocomplete
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Warning as WarningIcon,
    CheckCircle as ActiveIcon,
    CloudUpload as UploadIcon,
    Description as DocumentIcon,
    Visibility as ViewDocIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { useOrganization } from '../../context/OrganizationContext';
import { useProfile } from '../../context/userContext';
import { useAuth0 } from '@auth0/auth0-react';
import {
    getPilotLicenses,
    createPilotLicense,
    updatePilotLicense,
    deletePilotLicense
} from '../../network/apis/compliance.api';
import { useToast } from '../../hooks/useToast';

const LICENSE_TYPES = ['Part 107', '44809', 'Remote Pilot Certificate'];
const LICENSE_STATUSES = ['Active', 'Expired', 'Suspended', 'Revoked'];

const PilotLicenses = () => {
    const { currentOrganization, userRole, organizationMembers } = useOrganization();
    const { profile } = useProfile();
    const { getAccessTokenSilently } = useAuth0();
    const { toast, showSuccess, showError, hideToast } = useToast();

    const [licenses, setLicenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedLicense, setSelectedLicense] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
    const [viewingDocumentUrl, setViewingDocumentUrl] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [formData, setFormData] = useState({
        userId: '',
        licenseNumber: '',
        licenseType: 'Part 107',
        issueDate: '',
        expiryDate: '',
        status: 'Active',
        documentUrl: null
    });

    // Permission checks
    const isPilot = userRole?.toLowerCase() === 'pilot';
    const canManage = ['manager', 'admin', 'owner'].includes(userRole?.toLowerCase());
    const canDelete = ['admin', 'owner'].includes(userRole?.toLowerCase()); // Only admins and owners can delete

    useEffect(() => {
        if (currentOrganization?.organizationId) {
            fetchLicenses();
        }
    }, [currentOrganization]);

    const fetchLicenses = async () => {
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            const data = await getPilotLicenses(token);
            setLicenses(data.licenses || []);
        } catch (error) {
            showError('Failed to load licenses');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (license = null) => {
        setUploadError(null);
        if (license) {
            setEditMode(true);
            setSelectedLicense(license);

            // Find the user in organizationMembers for editing
            const user = organizationMembers.find(m => m.id === license.userId);
            setSelectedUser(user || null);

            setFormData({
                userId: license.userId,
                licenseNumber: license.licenseNumber,
                licenseType: license.licenseType,
                issueDate: license.issueDate?.split('T')[0] || '',
                expiryDate: license.expiryDate?.split('T')[0] || '',
                status: license.status,
                documentUrl: license.documentUrl || null
            });
        } else {
            setEditMode(false);
            setSelectedLicense(null);

            // For managers/admins/owners, start with no user selected
            // For pilots, auto-select themselves
            if (isPilot && !canManage) {
                const currentUser = organizationMembers.find(m => m.id === profile?.userId);
                setSelectedUser(currentUser || null);
                setFormData({
                    userId: profile?.userId || '',
                    licenseNumber: '',
                    licenseType: 'Part 107',
                    issueDate: '',
                    expiryDate: '',
                    status: 'Active',
                    documentUrl: null
                });
            } else {
                setSelectedUser(null);
                setFormData({
                    userId: '',
                    licenseNumber: '',
                    licenseType: 'Part 107',
                    issueDate: '',
                    expiryDate: '',
                    status: 'Active',
                    documentUrl: null
                });
            }
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedLicense(null);
        setSelectedUser(null);
        setEditMode(false);
        setUploadError(null);
    };

    const handleUserSelect = (event, newValue) => {
        setSelectedUser(newValue);
        setFormData({
            ...formData,
            userId: newValue?.id || ''
        });
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type (PDF or images)
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
            setUploadError('Please upload a PDF or image file (JPG, PNG)');
            return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            setUploadError('File size must be less than 10MB');
            return;
        }

        setUploadError(null);
        setUploading(true);

        try {
            const token = await getAccessTokenSilently();

            // Step 1: Request presigned URL from backend
            const urlResponse = await fetch(
                `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/files/upload-url`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        fileName: file.name,
                        fileType: file.type,
                        fileSize: file.size,
                        category: 'certificates'
                    })
                }
            );

            if (!urlResponse.ok) {
                const errorData = await urlResponse.json();
                throw new Error(errorData.error || 'Failed to get upload URL');
            }

            const { presignedUrl, fileUrl } = await urlResponse.json();

            // Step 2: Upload file directly to S3 using presigned URL
            const uploadResponse = await fetch(presignedUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': file.type
                },
                body: file
            });

            if (!uploadResponse.ok) {
                throw new Error('Failed to upload document');
            }

            // Step 3: Update form data with file URL
            setFormData({
                ...formData,
                documentUrl: fileUrl
            });

            showSuccess('Document uploaded successfully');

        } catch (err) {
            setUploadError(err.message);
        } finally {
            setUploading(false);
        }
    };

    const handleRemoveDocument = () => {
        setFormData({
            ...formData,
            documentUrl: null
        });
    };

    const handleViewDocument = async (documentUrl) => {
        try {

            // Extract the S3 key from the full URL
            // URL format: https://bucket.s3.region.amazonaws.com/key
            const url = new URL(documentUrl);
            const fileKey = url.pathname.substring(1); // Remove leading '/'

            const token = await getAccessTokenSilently();
            const requestUrl = `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/files/view-url?fileKey=${encodeURIComponent(fileKey)}`;

            const response = await fetch(requestUrl, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            const responseData = await response.json();

            if (response.ok) {
                // Backend returns { url: ... } not { presignedUrl: ... }
                const viewUrl = responseData.url || responseData.presignedUrl;
                setViewingDocumentUrl(viewUrl);
                setDocumentViewerOpen(true);
            } else {
                showError(responseData.error || 'Failed to load document');
            }
        } catch (error) {
            showError('Failed to load document: ' + error.message);
        }
    };

    const handleCloseDocumentViewer = () => {
        setDocumentViewerOpen(false);
        setViewingDocumentUrl(null);
    };

    const isImageFile = (url) => {
        if (!url) return false;
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
        return imageExtensions.some(ext => url.toLowerCase().includes(ext));
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async () => {
        // Validate required fields
        if (!formData.userId || !formData.licenseNumber || !formData.licenseType || !formData.issueDate || !formData.expiryDate) {
            showError('Please fill in all required fields including selecting a user');
            return;
        }

        try {
            const token = await getAccessTokenSilently();
            if (editMode) {
                await updatePilotLicense(token, selectedLicense.id, formData);
                showSuccess('License updated successfully');
            } else {
                await createPilotLicense(token, formData);
                showSuccess('License created successfully');
            }
            handleCloseDialog();
            fetchLicenses();
        } catch (error) {
            showError(error.message || 'Failed to save license');
        }
    };

    const handleOpenDeleteDialog = (license) => {
        setSelectedLicense(license);
        setDeleteDialogOpen(true);
    };

    const handleDelete = async () => {
        try {
            const token = await getAccessTokenSilently();
            await deletePilotLicense(token, selectedLicense.id);
            showSuccess('License deleted successfully');
            setDeleteDialogOpen(false);
            setSelectedLicense(null);
            fetchLicenses();
        } catch (error) {
            showError('Failed to delete license');
        }
    };

    const getLicenseStatus = (license) => {
        const expiryDate = new Date(license.expiryDate);
        const today = new Date();
        const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));

        if (license.status !== 'Active') {
            return { label: license.status, color: 'error' };
        }

        if (daysUntilExpiry < 0) {
            return { label: 'Expired', color: 'error' };
        } else if (daysUntilExpiry <= 30) {
            return { label: `Expiring in ${daysUntilExpiry} days`, color: 'warning' };
        } else {
            return { label: 'Active', color: 'success' };
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    // Filter licenses for pilots (only their own)
    const displayedLicenses = isPilot && !canManage
        ? licenses.filter(l => l.userId === profile?.userId)
        : licenses;

    // Check if pilot already has a license
    const hasOwnLicense = licenses.some(l => l.userId === profile?.userId);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        Pilot Licenses
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage Part 107 and recreational pilot licenses
                    </Typography>
                </Box>
                {/* Pilots can only add one license (their own), Managers/Admins/Owners can add licenses for any team member */}
                {(canManage || (isPilot && !hasOwnLicense)) && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        Add License
                    </Button>
                )}
            </Box>

            {displayedLicenses.length === 0 ? (
                <Alert severity="info">No licenses found. Add your first license to get started.</Alert>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Pilot</TableCell>
                                <TableCell>License Number</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Issue Date</TableCell>
                                <TableCell>Expiry Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Document</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayedLicenses.map((license) => {
                                const status = getLicenseStatus(license);
                                const canEdit = canManage || (isPilot && license.userId === profile?.userId);

                                return (
                                    <TableRow key={license.id}>
                                        <TableCell>
                                            {license.user?.firstName} {license.user?.lastName}
                                        </TableCell>
                                        <TableCell>{license.licenseNumber}</TableCell>
                                        <TableCell>{license.licenseType}</TableCell>
                                        <TableCell>{formatDate(license.issueDate)}</TableCell>
                                        <TableCell>{formatDate(license.expiryDate)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={status.color === 'success' ? <ActiveIcon /> : <WarningIcon />}
                                                label={status.label}
                                                color={status.color}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {license.documentUrl ? (
                                                <Tooltip title="View License Document">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleViewDocument(license.documentUrl)}
                                                    >
                                                        <DocumentIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    -
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {canEdit && (
                                                <Tooltip title="Edit License">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenDialog(license)}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {canDelete && (
                                                <Tooltip title="Delete License">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleOpenDeleteDialog(license)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editMode ? 'Edit License' : 'Add License'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        {/* User Selector - Only for managers/admins/owners when creating new license */}
                        {canManage && !editMode && (
                            <Autocomplete
                                value={selectedUser}
                                onChange={handleUserSelect}
                                options={organizationMembers}
                                getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Team Member"
                                        margin="normal"
                                        required
                                        helperText="Choose the team member this license belongs to"
                                    />
                                )}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                            />
                        )}

                        {/* For pilots or when editing, show read-only user info */}
                        {((isPilot && !canManage) || editMode) && (
                            <TextField
                                fullWidth
                                label="Pilot"
                                value={editMode
                                    ? `${selectedLicense?.user?.firstName} ${selectedLicense?.user?.lastName}`
                                    : `${profile?.firstName} ${profile?.lastName}`
                                }
                                margin="normal"
                                disabled
                                helperText={editMode ? "User cannot be changed when editing" : "Your license"}
                            />
                        )}

                        <TextField
                            fullWidth
                            label="License Number"
                            name="licenseNumber"
                            value={formData.licenseNumber}
                            onChange={handleChange}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            select
                            label="License Type"
                            name="licenseType"
                            value={formData.licenseType}
                            onChange={handleChange}
                            margin="normal"
                            required
                        >
                            {LICENSE_TYPES.map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            fullWidth
                            label="Issue Date"
                            name="issueDate"
                            type="date"
                            value={formData.issueDate}
                            onChange={handleChange}
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                        <TextField
                            fullWidth
                            label="Expiry Date"
                            name="expiryDate"
                            type="date"
                            value={formData.expiryDate}
                            onChange={handleChange}
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                        <TextField
                            fullWidth
                            select
                            label="Status"
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            margin="normal"
                            required
                        >
                            {LICENSE_STATUSES.map((status) => (
                                <MenuItem key={status} value={status}>
                                    {status}
                                </MenuItem>
                            ))}
                        </TextField>

                        {/* Document Upload Section */}
                        <Box sx={{ mt: 3, mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                License Document (Optional)
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                                Upload a copy of the pilot license (PDF or image, max 10MB)
                            </Typography>

                            {uploadError && (
                                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
                                    {uploadError}
                                </Alert>
                            )}

                            {formData.documentUrl ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <DocumentIcon color="primary" />
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                        Document uploaded
                                    </Typography>
                                    <Tooltip title="View Document">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleViewDocument(formData.documentUrl)}
                                        >
                                            <ViewDocIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Remove Document">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={handleRemoveDocument}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            ) : (
                                <Box>
                                    <input
                                        accept="application/pdf,image/jpeg,image/jpg,image/png"
                                        style={{ display: 'none' }}
                                        id="license-document-upload"
                                        type="file"
                                        onChange={handleFileSelect}
                                        disabled={uploading}
                                    />
                                    <label htmlFor="license-document-upload">
                                        <Button
                                            variant="outlined"
                                            component="span"
                                            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
                                            disabled={uploading}
                                            fullWidth
                                        >
                                            {uploading ? 'Uploading...' : 'Upload Document'}
                                        </Button>
                                    </label>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={uploading}>
                        {editMode ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete License</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this license? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Document Viewer Dialog */}
            <Dialog
                open={documentViewerOpen}
                onClose={handleCloseDocumentViewer}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">License Document</Typography>
                        <IconButton onClick={handleCloseDocumentViewer} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{
                        minHeight: '70vh',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        bgcolor: 'grey.100',
                        borderRadius: 1
                    }}>
                        {!viewingDocumentUrl && (
                            <CircularProgress />
                        )}
                        {viewingDocumentUrl && (
                            isImageFile(viewingDocumentUrl) ? (
                                <img
                                    src={viewingDocumentUrl}
                                    alt="License Document"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '70vh',
                                        objectFit: 'contain'
                                    }}
                                    onLoad={() => console.log('Image loaded successfully')}
                                    onError={(e) => console.error('Image failed to load:', e)}
                                />
                            ) : (
                                <object
                                    data={viewingDocumentUrl}
                                    type="application/pdf"
                                    style={{
                                        width: '100%',
                                        height: '70vh',
                                        border: 'none'
                                    }}
                                >
                                    <iframe
                                        src={viewingDocumentUrl}
                                        title="License Document"
                                        style={{
                                            width: '100%',
                                            height: '70vh',
                                            border: 'none'
                                        }}
                                        onLoad={() => console.log('PDF loaded successfully in iframe')}
                                        onError={(e) => console.error('PDF failed to load in iframe:', e)}
                                    />
                                </object>
                            )
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDocumentViewer}>Close</Button>
                    <Button
                        variant="contained"
                        onClick={() => window.open(viewingDocumentUrl, '_blank')}
                    >
                        Open in New Tab
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={hideToast}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={hideToast} severity={toast.severity} sx={{ width: '100%' }}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default PilotLicenses;
